/**
 * Detecting DocuSign's end-of-ceremony signal from a window ``message`` event
 * (GEN-5028).
 *
 * Classic embedded signing is framed on the standalone /sign page, and the
 * recipient view is minted with our SPA origin in ``messageOrigins`` — so
 * DocuSign posts messages to our parent window as the ceremony progresses. We
 * use that as a completion channel that does not depend on the post-sign
 * ``returnUrl`` redirect, which a browser can block when the signing page (a
 * public origin) tries to navigate the iframe to a private/local return URL
 * (Chrome Private Network Access), and which DocuSign can also fail to reach on
 * a decline. When the message says the session ended, the page returns the
 * clinician to their order itself.
 */

/** DocuSign hosts (demo + prod) whose messages we trust. */
const DOCUSIGN_HOST = /(^|\.)docusign\.(net|com)$/

/**
 * Tokens DocuSign uses for a terminal ``sessionEnd`` (`signing_complete`,
 * `decline`, `cancel`, `session_timeout`, `ttl_expired`, `viewing_complete`,
 * `exception`). Any of them means the ceremony is over and there is nothing left
 * to frame, so the clinician should go back to the order.
 *
 * Matched as whole tokens on the string payload or on ``sessionEndType`` /
 * ``event`` — never as a substring of the serialized object, which would treat
 * mid-ceremony flags like ``canCancel`` as session-end.
 */
const SESSION_END_TOKENS: ReadonlySet<string> = new Set([
  'signing_complete',
  'decline',
  'cancel',
  'session_timeout',
  'ttl_expired',
  'viewing_complete',
  'exception',
])

/** Whether an event ``origin`` is a DocuSign host. */
export const isDocusignOrigin = (origin: string): boolean => {
  try {
    return DOCUSIGN_HOST.test(new URL(origin).hostname)
  } catch {
    return false
  }
}

const tokenIfSessionEnd = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const token = value.trim().toLowerCase()
  return SESSION_END_TOKENS.has(token) ? token : null
}

/**
 * The session-end token DocuSign put on this payload, if any. Strings are the
 * token itself; objects carry it on ``sessionEndType`` or ``event`` (and the
 * same fields nested under ``data``).
 */
const sessionEndTokenFrom = (data: unknown): string | null => {
  const direct = tokenIfSessionEnd(data)
  if (direct) return direct
  if (data == null || typeof data !== 'object') return null
  const obj = data as Record<string, unknown>
  const nested = obj.data != null && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null
  return (
    tokenIfSessionEnd(obj.sessionEndType) ??
    tokenIfSessionEnd(obj.event) ??
    tokenIfSessionEnd(nested?.sessionEndType) ??
    tokenIfSessionEnd(nested?.event)
  )
}

/** The only token DocuSign uses for a genuinely signed ceremony. */
const SIGNING_COMPLETE_TOKEN = 'signing_complete'

/**
 * Whether a ``message`` event is DocuSign telling us the signing session ended.
 * Trusts only DocuSign origins, then looks for a terminal session-end token in
 * the payload (which DocuSign sends as a string or a structured object). Returns
 * false for the ready/progress messages sent mid-ceremony.
 */
export const isDocusignSessionEndMessage = (origin: string, data: unknown): boolean =>
  docusignSessionEndOutcome(origin, data) !== null

/**
 * The signing outcome from a DocuSign ``message`` event:
 * - ``'complete'``  — only ``signing_complete``; the ceremony produced a signature.
 * - ``'incomplete'`` — any other terminal token (decline, cancel, timeout, etc.);
 *   the session ended without a signature.
 * - ``null`` — not a DocuSign session-end message (non-DS origin, mid-ceremony,
 *   or unrecognised payload).
 *
 * Callers that need to distinguish outcome (e.g. showing "Signature Received"
 * only on a real completion) should use this rather than ``isDocusignSessionEndMessage``.
 */
export const docusignSessionEndOutcome = (origin: string, data: unknown): 'complete' | 'incomplete' | null => {
  if (!isDocusignOrigin(origin)) return null
  const token = sessionEndTokenFrom(data)
  if (token === null) return null
  return token === SIGNING_COMPLETE_TOKEN ? 'complete' : 'incomplete'
}
